import React, { useEffect } from 'react';
import { Button } from 'react-bootstrap';
import useBoundForm from '../hooks/useBoundForm.js';
import { useSelector } from 'react-redux';
import selectors from '../selectors/selectors.js';
import { icons } from '../images/index.js';
import BoundSelect from './boundForm/BoundSelect.js';
import BoundInput from './boundForm/BoundInput.js';
import { putServiceInterest } from '../redux/actions/leadsActions.js';
import { action } from '../lib/actionHelpers.js';
import mapServicesToCamelCase from '../lib/mapServicesToCamelCase.js';
import sortByProp from '../lib/sortByProp.js';
import setQuery from 'lib/setQuery.js';
import { logos } from '../images/index.js';
const formatService = (formState, services) => ({
  id: formState.id,
  serviceId: formState.serviceId,
  instanceId: formState.instanceId || Math.random(),
  multiplier: formState.multiplier,
  value: formState.amount,
  name: (services.find((n) => n.serviceId === formState.serviceId) || {}).name,
  serviceProviderId: formState.serviceProviderId || (services.find((n) => n.serviceId === formState.serviceId) || {}).service_provider_id,
});

const formatPayload = (data, type) => {
  if (type === 'delete') {
    return { service_interest_id: data.id };
  }
  return {
    service_multiplier: parseFloat(data.multiplier === 0) ? '1' : data.multiplier,
    service_provider_id: data.serviceProviderId,
    service_offered_id: data.serviceId,
    service_value: data.value,
    service_name: type === 'add' ? data.name : undefined,
    service_interest_id: type === 'edit' ? data.id : undefined,
  };
};

const ServiceInterestsEditor = (props) => {
  const pipelineLead = useSelector(selectors.leadModalActivePipelineLead) || {};
  const serviceProviderId = useSelector(selectors.serviceProviderId);
  const services = mapServicesToCamelCase(useSelector(selectors.serviceProviderServices));
  const activeLeadSyncPlatform = useSelector(selectors.activeLeadSyncPlatforms);
  const pipelineLeadId = pipelineLead.id;
  const isBound = !(!props.onChange && pipelineLeadId);
  const initialServices = !isBound ? mapServicesToCamelCase(pipelineLead.service_interests, serviceProviderId) : props.value || [];
  const form = useBoundForm({
    initialState: {
      services: initialServices,
      serviceProviderId,
    },
  });

  const serviceOptions = (services || [])
    .map((n) => ({ value: n.serviceId, label: n.label ? `${n.name} - ${n.label}` : n.name }))
    .filter((n) => !(form.state.services || []).map((n) => n.serviceId).includes(n.value) || form.state.serviceId === n.value)
    .sort(sortByProp('label'));
  const activeService = (form.state.services || []).find((n) => n.instanceId === form.state.instanceId && n.instanceId);
  const addIconTitle = activeService ? 'Save' : !form.state.serviceId ? 'Select a service to add' : 'Add';
  const serviceStateIsClean = !activeService && !form.state.serviceId;

  const update = (payload, type) => {
    payload = formatPayload(payload, type);
    if (type === 'delete') {
      action('DELETE_SERVICE_INTEREST', {
        id: payload.service_interest_id,
        pipelineLeadId,
      });
    }
    putServiceInterest(pipelineLeadId, payload, type);
  };

  useEffect(() => {
    if (form.state.services !== undefined && isBound) {
      if (JSON.stringify(form.state.services) !== JSON.stringify(initialServices)) {
        props.onChange(form.state.services);
      }
    }
  }, [form.state.services]);

  useEffect(() => {
    if (JSON.stringify(form.state.services) !== JSON.stringify(initialServices)) {
      form.setState({ services: initialServices || [] });
      clearActiveService();
    }
  }, [initialServices]);

  const clearActiveService = () => {
    ['id', 'serviceId', 'multiplier', 'amount', 'instanceId'].forEach((n) => form.set(n, null));
  };

  const handleAddClick = () => {
    if (!form.state.serviceId) {
      return;
    }
    const modifiedService = formatService(form.state, services);
    if (!isBound) {
      const action = activeService ? 'edit' : 'add';
      update(modifiedService, action);
    }
    const modifiedServices = activeService ? (form.state.services || []).map((n) => (n.instanceId !== form.state.instanceId ? n : modifiedService)) : [...form.state.services, modifiedService];
    form.set('services', modifiedServices);
    clearActiveService();
  };

  const handleServicesChange = (serviceId) => {
    const service = services.find((n) => n.serviceId === serviceId) || {};
    if (!form.state.amount && !form.state.multiplier) {
      form.setState({
        ...form.state,
        serviceId: serviceId,
        amount: service.value,
        multiplier: service.multiplier,
      });
    }
  };

  const handleTagClick = (e, n) => {
    const service = form.state.services.find((o) => o.instanceId === n.instanceId) || {};
    form.setState({
      ...form.state,
      id: service.id,
      instanceId: service.instanceId,
      serviceId: service.serviceId,
      amount: service.value,
      multiplier: service.multiplier,
    });
  };

  const handleTagDeleteClick = (e, tag) => {
    e.preventDefault();
    e.stopPropagation();
    const service = form.state.services.find((o) => o.instanceId === tag.instanceId);
    if (service && !isBound) {
      update(service, 'delete');
    }
    form.set(
      'services',
      (form.state.services || []).filter((n) => n.instanceId !== tag.instanceId)
    );
    if (tag.instanceId === form.state.instanceId) {
      clearActiveService();
    }
  };

  const handleCancelClick = () => {
    clearActiveService();
  };

  return (
    <div className={`service-interests-editor ${serviceStateIsClean ? 'clean' : ''}`}>
      <div className={'service-interests-form'}>
        <div className={`heading ${activeLeadSyncPlatform === 'nextech' ? 'nextech-btn' : ''}`}>
          {!activeService && <span>Add Service</span>}
          {activeService && (
            <>
              <span>Edit {(activeService || {}).name}</span>
              <span className={'cancel'} onClick={handleCancelClick}>
                Cancel
              </span>
            </>
          )}
          {activeLeadSyncPlatform === 'nextech' ? (
            <div onClick={() => setQuery({ ty: 'nextech', ia: 'sync-payments' })} className="nextech-services-sync">
              <img alt={'Icon'} width={12} height={12} src={logos[`nextech`]} />
              <p>Sync Payment</p>
            </div>
          ) : null}
        </div>
        <div className={'fields'}>
          <BoundSelect formId={form.id} id={'serviceId'} placeholder={'Services Offered'} options={serviceOptions} onChange={handleServicesChange} />
          <BoundInput formId={form.id} id={'multiplier'} placeholder={'Multiplier'} disabled={!form.state.serviceId} />
          <BoundInput formId={form.id} id={'amount'} placeholder={'Amount'} disabled={!form.state.serviceId} />
          <div className={`icon-wrapper ${activeService ? 'save-icon' : ''}`} onClick={handleAddClick}>
            <img src={icons[activeService ? 'icon-save' : 'icon-plus-black']} data-title={addIconTitle} alt={''} />
          </div>
        </div>
      </div>
      {(form.state.services || []).length > 0 && (
        <div className={'service-interests-tags'}>
          {(form.state.services || []).map((n, idx) => (
            <Button key={idx} className={n.instanceId === form.state.instanceId && n.instanceId ? 'active' : ''} variant={'primary'} onClick={(e) => handleTagClick(e, n)}>
              <span>
                {n.multiplier > 1 && (
                  <>
                    <span>{n.multiplier}</span>
                    <span style={{ position: 'relative', bottom: '1px', fontSize: '9px' }}>{' x '}</span>
                  </>
                )}
                {`${n.name} $${parseInt(n.value, 10).toLocaleString()} `}
              </span>
              <img src={icons[form.state.serviceId === n.serviceId ? 'icon-delete-black' : 'icon-delete-white']} data-title={'Delete'} onClick={(e) => handleTagDeleteClick(e, n)} alt={''} />
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ServiceInterestsEditor;
